import { useEffect, useState } from 'react';
import { Mail, Phone, Tag } from 'lucide-react';
import { getDeals } from '@generated/server/deals';
import { TwoferDeal } from '@generated/components/TwoferDeal';
import { NewBusinessDeal } from '@generated/components/NewBusinessDeal';
import { ClaimDeal } from '@generated/components/ClaimDeal';
import { termsForBoardDeal, type DealTerms } from '@generated/components/dealCatalog';

type Deal = Awaited<ReturnType<typeof getDeals>>['items'][number];

/** Green "special deals" panel on the Display Advertising page; deals are written on the Special Deals board. */
export function SpecialDeals() {
  const [deals, setDeals] = useState<Deal[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [claim, setClaim] = useState<DealTerms | null>(null);
  useEffect(() => {
    let on = true;
    getDeals({ data: {} }).then((r) => { if (on) setDeals(r?.items ?? []); })
      .catch((e) => { console.error(e); if (on) setError('Our deals could not be loaded right now. Please call 917-652-9128.'); })
      .finally(() => { if (on) setLoading(false); });
    return () => { on = false; };
  }, []);

  const ask = (d: Deal) => `mailto:gbrook8344@gmail.com?subject=${encodeURIComponent(`Special deal: ${d.name}`)}&body=${encodeURIComponent(`Hello George,\n\nI'm interested in the "${d.name}" deal${d.dealPrice ? ` (${d.dealPrice})` : ''}.\n\nBusiness:\nCallback number:\n`)}`;

  return (
    <section className="mb-10">
      <ClaimDeal deal={claim} onClose={() => setClaim(null)} />
      <TwoferDeal onClaim={setClaim} />
      <NewBusinessDeal onClaim={setClaim} />
      <h3 className="font-[family-name:var(--font-heading)] text-2xl font-semibold text-[hsl(var(--brand-green))]">This month's special deals</h3>
      {loading && <div className="mt-4 grid gap-4 sm:grid-cols-2">{[0, 1].map((k) => <div key={k} className="h-40 animate-pulse border border-border bg-muted" />)}</div>}
      {error && <p role="alert" className="mt-4 text-sm text-destructive">{error}</p>}
      {!loading && !error && deals.length === 0 && <p className="mt-4 text-sm text-muted-foreground">No special deals are running right now. Call 917-652-9128 and ask what we can do for you.</p>}
      <div className="mt-4 grid gap-5 pr-1.5 sm:grid-cols-2">
        {deals.map((d) => (
          <article key={d.id} className="flex flex-col border-2 border-foreground bg-card p-5 shadow-[6px_6px_0_0_hsl(var(--brand-purple))]">
            <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-[hsl(var(--brand-purple))]"><Tag className="h-3.5 w-3.5" />{d.paper ?? 'Special deal'}</div>
            <h4 className="mt-1 font-[family-name:var(--font-heading)] text-xl font-semibold">{d.name}</h4>
            {d.description && <p className="mt-2 text-sm text-muted-foreground">{d.description}</p>}
            <div className="mt-3 flex flex-wrap items-baseline gap-x-3">
              {d.dealPrice && <span className="font-[family-name:var(--font-heading)] text-2xl font-bold text-[hsl(var(--brand-purple))]">{d.dealPrice}</span>}
              {d.regularPrice && <span className="text-sm text-muted-foreground line-through">{d.regularPrice}</span>}
            </div>
            {d.validUntil && <p className="mt-1 text-xs text-muted-foreground">Good through {new Date(d.validUntil).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric', timeZone: 'UTC' })}</p>}
            <div className="mt-auto flex flex-wrap gap-2 pt-4">
              {termsForBoardDeal(d.name) ? <button type="button" onClick={() => { const t = termsForBoardDeal(d.name); if (t) setClaim(t); }} className="inline-flex min-h-11 items-center gap-2 bg-[hsl(var(--brand-green))] px-4 text-sm font-semibold text-primary-foreground hover:opacity-90"><Mail className="h-4 w-4" />Claim this deal</button> : <a href={ask(d)} className="inline-flex min-h-11 items-center gap-2 bg-[hsl(var(--brand-green))] px-4 text-sm font-semibold text-primary-foreground hover:opacity-90"><Mail className="h-4 w-4" />Claim this deal</a>}
              <a href="tel:9176529128" className="inline-flex min-h-11 items-center gap-2 border border-foreground px-4 text-sm font-semibold"><Phone className="h-4 w-4" />Call George</a>
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}
